import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, Save, CalendarDays, Check, X, Inbox, MailOpen, ImageIcon, Receipt, Plus, CircleDollarSign, TrendingUp } from "lucide-react";
import { API } from "../App";
import Reveal from "../components/Reveal";

const EMPTY = {
  price_mode: "auto",
  manual_price_per_gram_24k: 192.22,
  payout_percent: 90,
  silver_price_mode: "auto",
  manual_silver_price_per_gram: 2.79,
  silver_payout_percent: 65,
  platinum_price_mode: "auto",
  manual_platinum_price_per_gram: 73,
  platinum_payout_percent: 60,
  phone: "514-378-8833",
  email: "estefanijewellery@gmail.com",
  play_store_url: "",
  app_store_url: "",
  apk_url: "",
  download_note: "Bientôt disponible sur Google Play et l'App Store",
};

const STATUS = {
  en_attente: { label: "En attente d'appel", cls: "bg-gold/15 text-goldlight border-gold/30" },
  confirmee: { label: "Confirmée", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30" },
  annulee: { label: "Annulée", cls: "bg-cream/5 text-mutedc border-cream/15" },
};

const INV_STATUS = {
  en_attente: { label: "En attente de paiement", cls: "bg-gold/15 text-goldlight border-gold/30" },
  payee: { label: "Payée", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30" },
};

function Spark({ series }) {
  const max = Math.max(...series.map((s) => s.count), 1);
  return (
    <div className="flex items-end gap-1 h-10 mt-4" data-testid="admin-stats-spark">
      {series.map((s, i) => (
        <div
          key={s.day}
          title={`${s.day} : ${s.count}`}
          className="flex-1 rounded-sm bg-gradient-to-t from-golddeep to-gold hover:opacity-80 transition-opacity"
          style={{ height: `${Math.max(5, (s.count / max) * 100)}%`, opacity: 0.45 + (i / series.length) * 0.55 }}
        />
      ))}
    </div>
  );
}

const fmt = (v) => Number(v || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("gce_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState({ totals: {}, daily: {} });
  const [invForm, setInvForm] = useState({ client_name: "", client_contact: "", description: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = async (t) => {
    const h = { Authorization: `Bearer ${t}` };
    try {
      const s = await axios.get(`${API}/settings`);
      setForm({ ...EMPTY, ...s.data });
      const r = await axios.get(`${API}/admin/appointments`, { headers: h });
      setAppointments(r.data);
      const m = await axios.get(`${API}/admin/collection-messages`, { headers: h });
      setMessages(m.data);
      const f = await axios.get(`${API}/admin/invoices`, { headers: h });
      setInvoices(f.data);
      const a = await axios.get(`${API}/admin/analytics`, { headers: h });
      setAnalytics(a.data || { totals: {}, daily: {} });
    } catch {
      localStorage.removeItem("gce_token");
      setToken("");
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      const t = r.data.token;
      localStorage.setItem("gce_token", t);
      setToken(t);
      toast.success("Bienvenue");
      loadAll(t);
    } catch (err) {
      if (err.response?.status === 401) toast.error("Identifiants incorrects");
      else if (err.response) toast.error(`Erreur serveur (${err.response.status}) — réessayez`);
      else toast.error("Impossible de contacter le serveur — vérifiez votre connexion");
      console.error("Login error:", err);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["manual_price_per_gram_24k", "payout_percent", "manual_silver_price_per_gram", "silver_payout_percent", "manual_platinum_price_per_gram", "platinum_payout_percent"].forEach(
        (k) => (payload[k] = Number(payload[k]))
      );
      await axios.put(`${API}/admin/settings`, payload, { headers });
      toast.success("Réglages enregistrés");
      loadAll(token);
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/appointments/${id}`, { status }, { headers });
      setAppointments((a) => a.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "confirmee" ? "Rendez-vous confirmé" : "Rendez-vous annulé");
    } catch {
      toast.error("Action impossible");
    }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/collection-messages/${id}`, { status: "lu" }, { headers });
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, status: "lu" } : x)));
      toast.success("Marqué comme lu");
    } catch {
      toast.error("Action impossible");
    }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/invoices`,
        { ...invForm, amount: Number(invForm.amount) },
        { headers }
      );
      toast.success("Facture créée");
      setInvForm({ client_name: "", client_contact: "", description: "", amount: "" });
      loadAll(token);
    } catch {
      toast.error("Création impossible — vérifiez les champs");
    }
  };

  const setInvoiceStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/invoices/${id}`, { status }, { headers });
      setInvoices((f) => f.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "payee" ? "Facture marquée payée" : "Facture remise en attente");
    } catch {
      toast.error("Action impossible");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères");
    if (pw.next !== pw.confirm) return toast.error("Les deux nouveaux mots de passe ne correspondent pas");
    setPwBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pw.current, new_password: pw.next },
        { headers }
      );
      toast.success("Mot de passe modifié");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expirée — reconnectez-vous");
        logout();
      } else toast.error(err.response?.data?.detail || "Modification impossible");
    } finally {
      setPwBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gce_token");
    setToken("");
    setAppointments([]);
    setMessages([]);
    setInvoices([]);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setInv = (k) => (e) => setInvForm({ ...invForm, [k]: e.target.value });
  const field = "mt-1.5 w-full rounded-lg border border-cream/15 bg-ink/70 px-4 py-3 text-cream outline-none focus:border-gold transition-colors";
  const label = "text-xs uppercase tracking-[0.2em] text-mutedc";

  if (!token)
    return (
      <main className="min-h-screen flex items-center justify-center px-5 pt-[72px]" data-testid="admin-login-view">
        <Reveal>
          <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-gold/20 bg-panel/80 p-9" data-testid="admin-login-form">
            <Lock size={26} className="text-gold" />
            <h1 className="mt-5 font-display text-3xl text-cream">Espace administrateur</h1>
            <p className="mt-2 text-sm text-mutedc">Accès réservé : messages, factures, rendez-vous et taux.</p>
            <div className="mt-6 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Courriel"
                data-testid="admin-login-email-input"
                className={field}
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                data-testid="admin-login-input"
                className={field}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink hover:bg-goldlight transition-all disabled:opacity-60"
            >
              {busy && <Loader2 size={15} className="animate-spin" />} Se connecter
            </button>
          </form>
        </Reveal>
      </main>
    );

  const unread = messages.filter((m) => m.status === "nouveau").length;
  const unpaid = invoices.filter((f) => f.status === "en_attente").length;
  const totals = analytics?.totals || {};
  const daily = analytics?.daily || {};
  const last7 = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const sum7 = (prefix) =>
    Object.entries(daily)
      .filter(([k]) => k === prefix || k.startsWith(prefix + ":"))
      .reduce((acc, [, days]) => acc + Object.entries(days).filter(([d]) => d >= last7 && d !== "total").reduce((s, [, c]) => s + c, 0), 0);
  const stats7 = { visit: sum7("visit"), calc: sum7("calc"), download: sum7("download") };
  const totalVisits = Object.entries(totals)
    .filter(([k]) => k === "visit" || k.startsWith("visit:"))
    .reduce((s, [, c]) => s + c, 0);
  const totalDownloads = (totals["download:play"] ?? 0) + (totals["download:appstore"] ?? 0) + (totals["download:apk"] ?? 0);
  const series14 = (prefix) => {
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      let c = 0;
      Object.entries(daily).forEach(([k, days]) => {
        if (k === prefix || k.startsWith(prefix + ":")) c += days[d] || 0;
      });
      out.push({ day: d, count: c });
    }
    return out;
  };

  return (
    <main className="pt-[72px] pb-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="font-display text-4xl text-cream" data-testid="admin-title">Gestion</h1>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-cream/20 px-5 py-2.5 text-sm text-cream/80 hover:border-gold hover:text-goldlight transition-all">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>

        <section className="mt-10 rounded-3xl border border-emerald-400/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-messages-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <Inbox size={20} className="text-emerald-300" />
            <h2 className="font-display text-2xl text-goldlight">Messagerie — Objets & collections ({messages.length})</h2>
            {unread > 0 && (
              <span className="rounded-full bg-emerald-400/15 border border-emerald-400/40 px-3 py-1 text-[11px] text-emerald-300" data-testid="admin-messages-unread-badge">
                {unread} nouveau(x)
              </span>
            )}
          </div>
          {messages.length === 0 ? (
            <p className="mt-5 text-sm text-mutedc">Aucun message pour le moment.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-2xl border p-6 ${m.status === "nouveau" ? "border-emerald-400/35 bg-emerald-400/5" : "border-cream/10 bg-ink/40"}`}
                  data-testid="admin-message-card"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-cream" data-testid="admin-message-name">
                        {m.name}
                        {m.status === "nouveau" && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-emerald-400 align-middle" />}
                      </p>
                      <p className="mt-0.5 text-sm text-cream/60 tabular">
                        {m.phone} · {m.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {m.estimation && (
                        <span className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] text-goldlight" data-testid="admin-message-estimation-badge">
                          Estimation demandée
                        </span>
                      )}
                      {m.status === "nouveau" && (
                        <button
                          onClick={() => markRead(m.id)}
                          data-testid="admin-message-read-btn"
                          className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-3.5 py-1.5 text-xs text-cream/75 hover:border-gold hover:text-goldlight transition-all"
                        >
                          <MailOpen size={13} /> Marquer comme lu
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-4 whitespace-pre-line rounded-xl bg-ink/60 border border-cream/10 p-4 text-sm leading-relaxed text-cream/85" data-testid="admin-message-text">
                    {m.message}
                  </p>
                  {m.images?.length > 0 && (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-mutedc">
                        <ImageIcon size={13} /> {m.images.length} photo(s)
                      </span>
                      {m.images.map((p) => (
                        <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" data-testid="admin-message-image-link">
                          <img
                            src={`${API}/files/${p}`}
                            alt="Objet envoyé"
                            className="h-20 w-20 rounded-lg object-cover border border-gold/25 hover:border-gold transition-all"
                            data-testid="admin-message-image"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-10 rounded-3xl border border-gold/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-invoices-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <Receipt size={20} className="text-gold" />
            <h2 className="font-display text-2xl text-goldlight">Factures ({invoices.length})</h2>
            {unpaid > 0 && (
              <span className="rounded-full bg-gold/15 border border-gold/40 px-3 py-1 text-[11px] text-goldlight" data-testid="admin-invoices-unpaid-badge">
                {unpaid} en attente de paiement
              </span>
            )}
          </div>

          <form onSubmit={createInvoice} className="mt-6 rounded-2xl border border-cream/10 bg-ink/40 p-5 sm:p-6" data-testid="admin-invoice-form">
            <p className="text-sm text-cream/80 inline-flex items-center gap-2">
              <Plus size={15} className="text-gold" /> Nouvelle facture
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={label}>Nom du client</label>
                <input type="text" required value={invForm.client_name} onChange={setInv("client_name")} placeholder="Nom du client" data-testid="admin-invoice-client-input" className={field} />
              </div>
              <div>
                <label className={label}>Contact (courriel / téléphone)</label>
                <input type="text" value={invForm.client_contact} onChange={setInv("client_contact")} placeholder="Optionnel" data-testid="admin-invoice-contact-input" className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Description (articles vendus)</label>
                <textarea rows="3" required value={invForm.description} onChange={setInv("description")} placeholder="Ex. : Montre ancienne 1 200 $ + collier or 18K 850 $" data-testid="admin-invoice-description-input" className={`${field} resize-none`} />
              </div>
              <div>
                <label className={label}>Montant total ($ CA)</label>
                <input type="number" step="0.01" min="0.01" required value={invForm.amount} onChange={setInv("amount")} placeholder="0.00" data-testid="admin-invoice-amount-input" className={field} />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-invoice-create-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink hover:bg-goldlight transition-all disabled:opacity-60"
            >
              <Plus size={15} /> Créer la facture
            </button>
          </form>

          {invoices.length === 0 ? (
            <p className="mt-6 text-sm text-mutedc">Aucune facture pour le moment.</p>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[720px]" data-testid="admin-invoices-table">
                <thead className="text-xs uppercase tracking-wider text-mutedc border-b border-gold/15">
                  <tr>
                    <th className="py-3 pr-4">Numéro</th>
                    <th className="py-3 pr-4">Client</th>
                    <th className="py-3 pr-4">Description</th>
                    <th className="py-3 pr-4">Montant</th>
                    <th className="py-3 pr-4">Statut</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((f) => (
                    <tr key={f.id} className="border-b border-cream/5 text-cream/85 align-top" data-testid="admin-invoice-row">
                      <td className="py-3 pr-4 tabular text-cream/70" data-testid="admin-invoice-number">{f.number}</td>
                      <td className="py-3 pr-4 font-medium">{f.client_name}<br /><span className="text-xs text-mutedc">{f.client_contact}</span></td>
                      <td className="py-3 pr-4 text-cream/70 max-w-[260px]">{f.description}</td>
                      <td className="py-3 pr-4 tabular text-goldlight">{fmt(f.amount)}</td>
                      <td className="py-3 pr-4">
                        <span className={`inline-block rounded-full border px-3 py-1 text-[11px] ${INV_STATUS[f.status]?.cls || ""}`}>
                          {INV_STATUS[f.status]?.label || f.status}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2 flex-wrap">
                          <a
                            href={`/facture/${f.id}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="admin-invoice-view-link"
                            className="rounded-full border border-cream/20 px-3.5 py-1.5 text-xs text-cream/75 hover:border-gold hover:text-goldlight transition-all"
                          >
                            Voir
                          </a>
                          {f.status === "en_attente" ? (
                            <button
                              onClick={() => setInvoiceStatus(f.id, "payee")}
                              data-testid="admin-invoice-pay-btn"
                              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 px-3.5 py-1.5 text-xs text-emerald-300 hover:bg-emerald-400/10 transition-all"
                            >
                              <CircleDollarSign size={13} /> Payée
                            </button>
                          ) : (
                            <button
                              onClick={() => setInvoiceStatus(f.id, "en_attente")}
                              data-testid="admin-invoice-unpay-btn"
                              className="rounded-full border border-cream/20 px-3.5 py-1.5 text-xs text-mutedc hover:border-gold hover:text-goldlight transition-all"
                            >
                              Remettre en attente
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-10 rounded-3xl border border-gold/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-appointments-panel">
          <div className="flex items-center gap-3">
            <CalendarDays size={20} className="text-gold" />
            <h2 className="font-display text-2xl text-goldlight">Rendez-vous — Or ({appointments.length})</h2>
          </div>
          {appointments.length === 0 ? (
            <p className="mt-5 text-sm text-mutedc">Aucun rendez-vous demandé pour le moment.</p>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[760px]" data-testid="admin-appointments-table">
                <thead className="text-xs uppercase tracking-wider text-mutedc border-b border-gold/15">
                  <tr>
                    <th className="py-3 pr-4">Nom</th>
                    <th className="py-3 pr-4">Téléphone</th>
                    <th className="py-3 pr-4">Courriel</th>
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Heure</th>
                    <th className="py-3 pr-4">Localisation</th>
                    <th className="py-3 pr-4">Détails</th>
                    <th className="py-3 pr-4">Statut</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((a) => (
                    <tr key={a.id} className="border-b border-cream/5 text-cream/85 align-top" data-testid="admin-appointment-row">
                      <td className="py-3 pr-4 font-medium">{a.name}</td>
                      <td className="py-3 pr-4 tabular">{a.phone}</td>
                      <td className="py-3 pr-4">{a.email}</td>
                      <td className="py-3 pr-4 tabular">{a.date}</td>
                      <td className="py-3 pr-4 tabular">{a.time}</td>
                      <td className="py-3 pr-4">{a.location}</td>
                      <td className="py-3 pr-4 text-cream/60">{a.details || "—"}</td>
                      <td className="py-3 pr-4">
                        <span className={`inline-block rounded-full border px-3 py-1 text-[11px] ${STATUS[a.status]?.cls || ""}`}>
                          {STATUS[a.status]?.label || a.status}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setStatus(a.id, "confirmee")}
                            data-testid="admin-appointment-confirm-btn"
                            title="Confirmer"
                            className="rounded-full border border-emerald-400/40 p-2 text-emerald-300 hover:bg-emerald-400/10 transition-all"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setStatus(a.id, "annulee")}
                            data-testid="admin-appointment-cancel-btn"
                            title="Annuler"
                            className="rounded-full border border-cream/20 p-2 text-mutedc hover:border-red-400/50 hover:text-red-300 transition-all"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-10 rounded-3xl border border-gold/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-analytics-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <TrendingUp size={20} className="text-gold" />
            <h2 className="font-display text-2xl text-goldlight">Statistiques</h2>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            <div className="rounded-2xl border border-gold/15 bg-ink/50 p-6" data-testid="admin-stats-visits">
              <p className="text-xs uppercase tracking-[0.2em] text-mutedc">Visites du site</p>
              <p className="mt-2 font-display text-4xl text-cream tabular" data-testid="admin-stats-visits-total">{totalVisits}</p>
              <p className="mt-2 text-xs text-mutedc">7 derniers jours : {stats7.visit ?? 0}</p>
              <Spark series={series14("visit")} />
            </div>
            <div className="rounded-2xl border border-gold/15 bg-ink/50 p-6" data-testid="admin-stats-calc">
              <p className="text-xs uppercase tracking-[0.2em] text-mutedc">Calculs d'or</p>
              <p className="mt-2 font-display text-4xl text-cream tabular">{totals.calc ?? 0}</p>
              <p className="mt-2 text-xs text-mutedc">7 derniers jours : {stats7.calc ?? 0}</p>
              <Spark series={series14("calc")} />
            </div>
            <div className="rounded-2xl border border-gold/15 bg-ink/50 p-6" data-testid="admin-stats-downloads">
              <p className="text-xs uppercase tracking-[0.2em] text-mutedc">Téléchargements de l'app</p>
              <p className="mt-2 font-display text-4xl text-cream tabular">{totalDownloads}</p>
              <p className="mt-2 text-xs text-mutedc">7 derniers jours : {stats7.download ?? 0}</p>
              <Spark series={series14("download")} />
            </div>
          </div>
          <div className="mt-5 grid gap-2 text-sm text-cream/70 sm:grid-cols-2">
            <p data-testid="admin-stats-downloads-breakdown">
              Clics de téléchargement — Google Play : {totals["download:play"] ?? 0} · App Store : {totals["download:appstore"] ?? 0} · APK : {totals["download:apk"] ?? 0}
            </p>
            <p data-testid="admin-stats-visits-breakdown">
              Pages — Accueil : {totals["visit:/"] ?? 0} · Infos : {totals["visit:/learn"] ?? 0} · Nous achetons : {totals["visit:/nous-achetons"] ?? 0} · Télécharger : {totals["visit:/telecharger"] ?? 0}
            </p>
          </div>
        </section>

        <section className="mt-10 rounded-3xl border border-gold/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-settings-panel">
          <h2 className="font-display text-2xl text-goldlight">Taux & paiements</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label className={label}>Mode du prix de l'or</label>
              <select value={form.price_mode} onChange={set("price_mode")} data-testid="admin-price-mode-select" className={field}>
                <option value="auto" className="bg-ink">Auto (cours en direct)</option>
                <option value="manual" className="bg-ink">Manuel</option>
              </select>
            </div>
            <div>
              <label className={label}>Prix manuel or 24K ($/g)</label>
              <input type="number" step="0.01" value={form.manual_price_per_gram_24k} onChange={set("manual_price_per_gram_24k")} data-testid="admin-manual-gold-input" className={field} />
            </div>
            <div>
              <label className={label}>Taux de rachat or (%)</label>
              <input type="number" step="0.1" value={form.payout_percent} onChange={set("payout_percent")} data-testid="admin-payout-input" className={field} />
            </div>
            <div>
              <label className={label}>Mode du prix de l'argent</label>
              <select value={form.silver_price_mode} onChange={set("silver_price_mode")} data-testid="admin-silver-mode-select" className={field}>
                <option value="auto" className="bg-ink">Auto (cours en direct)</option>
                <option value="manual" className="bg-ink">Manuel</option>
              </select>
            </div>
            <div>
              <label className={label}>Prix manuel argent ($/g)</label>
              <input type="number" step="0.01" value={form.manual_silver_price_per_gram} onChange={set("manual_silver_price_per_gram")} data-testid="admin-manual-silver-input" className={field} />
            </div>
            <div>
              <label className={label}>Taux de rachat argent (%)</label>
              <input type="number" step="0.1" value={form.silver_payout_percent} onChange={set("silver_payout_percent")} data-testid="admin-silver-payout-input" className={field} />
            </div>
            <div>
              <label className={label}>Mode du prix du platine</label>
              <select value={form.platinum_price_mode} onChange={set("platinum_price_mode")} data-testid="admin-platinum-mode-select" className={field}>
                <option value="auto" className="bg-ink">Auto (cours en direct)</option>
                <option value="manual" className="bg-ink">Manuel</option>
              </select>
            </div>
            <div>
              <label className={label}>Prix manuel platine ($/g)</label>
              <input type="number" step="0.01" value={form.manual_platinum_price_per_gram} onChange={set("manual_platinum_price_per_gram")} data-testid="admin-manual-platinum-input" className={field} />
            </div>
            <div>
              <label className={label}>Taux de rachat platine (%)</label>
              <input type="number" step="0.1" value={form.platinum_payout_percent} onChange={set("platinum_payout_percent")} data-testid="admin-platinum-payout-input" className={field} />
            </div>
          </div>

          <h2 className="mt-10 font-display text-2xl text-goldlight">Téléchargement de l'application</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label className={label}>Lien Google Play</label>
              <input type="url" value={form.play_store_url} onChange={set("play_store_url")} placeholder="https://play.google.com/..." data-testid="admin-play-url-input" className={field} />
            </div>
            <div>
              <label className={label}>Lien App Store</label>
              <input type="url" value={form.app_store_url} onChange={set("app_store_url")} placeholder="https://apps.apple.com/..." data-testid="admin-appstore-url-input" className={field} />
            </div>
            <div>
              <label className={label}>Lien fichier APK</label>
              <input type="url" value={form.apk_url} onChange={set("apk_url")} placeholder="https://.../app.apk" data-testid="admin-apk-url-input" className={field} />
            </div>
            <div>
              <label className={label}>Note sous les boutons</label>
              <input type="text" value={form.download_note} onChange={set("download_note")} data-testid="admin-download-note-input" className={field} />
            </div>
          </div>

          <h2 className="mt-10 font-display text-2xl text-goldlight">Contact</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label className={label}>Téléphone</label>
              <input type="text" value={form.phone} onChange={set("phone")} data-testid="admin-phone-input" className={field} />
            </div>
            <div>
              <label className={label}>Courriel</label>
              <input type="email" value={form.email} onChange={set("email")} data-testid="admin-email-input" className={field} />
            </div>
          </div>

          <button
            onClick={save}
            disabled={busy}
            data-testid="admin-save-settings"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-ink hover:bg-goldlight transition-all disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer
          </button>
        </section>

        <section className="mt-10 rounded-3xl border border-gold/20 bg-panel/70 p-7 sm:p-10" data-testid="admin-change-password-panel">
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-gold" />
            <h2 className="font-display text-2xl text-goldlight">Changer le mot de passe</h2>
          </div>
          <form onSubmit={changePassword} className="mt-6 grid gap-5 sm:grid-cols-3">
            <div>
              <label className={label}>Mot de passe actuel</label>
              <input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="admin-change-password-current-input" className={field} />
            </div>
            <div>
              <label className={label}>Nouveau mot de passe</label>
              <input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="admin-change-password-new-input" className={field} />
            </div>
            <div>
              <label className={label}>Confirmer</label>
              <input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="admin-change-password-confirm-input" className={field} />
            </div>
            <div className="sm:col-span-3">
              <button
                type="submit"
                disabled={pwBusy || !pw.current || !pw.next}
                data-testid="admin-change-password-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-ink hover:bg-goldlight transition-all disabled:opacity-60"
              >
                {pwBusy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Mettre à jour le mot de passe
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
